import {
  getApiTradeV1TradeCompaniesPackagingSpecificationsRetrieveQueryKey,
  SimplePackagingSpecification,
  useApiTradeV1TradeCompaniesPackagingSpecificationsRetrieve,
  useQueryClient,
} from 'api';
import { useToggle } from 'features';
import { useFormContext, useWatch } from 'form';
import { useTranslation } from 'i18n';
import { useState } from 'react';
import { Box, Button, CardsListSkeleton, Stack, Typography } from 'ui';
import { Add } from 'ui/icons';

import { useDeletePackagingSpecification } from '../../hooks/useDeletePackagingSpecification/useDeletePackagingSpecification';
import { BuyerMaterialSpecificationFormType } from '../../pages/schema';
import { CreatePackagingSpecificationDialog } from '../CreatePackagingSpecificationDialog/CreatePackagingSpecificationDialog';
import { PackagingSpecificationsCard } from '../PackagingSpecificationsCard/PackagingSpecificationsCard';
import { UpdatePackagingSpecificationDialog } from '../UpdatePackagingSpecificationDialog/UpdatePackagingSpecificationDialog';

const NoPackagingSpecifications = () => {
  const { t } = useTranslation('orders');

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Typography color="secondary">{t('buyerMaterialSpecifications.packagingSpecificationList.empty')}</Typography>
    </Box>
  );
};

export const PackagingSpecificationList = () => {
  const { t } = useTranslation('orders');
  const queryClient = useQueryClient();
  const {
    isOpen: isCreatePackSpecificationsFormOpen,
    open: openCreatePackSpecificationForm,
    close: closeCreatePackSpecificationForm,
  } = useToggle();
  const {
    isOpen: isUpdatePackSpecificationsFormOpen,
    open: openUpdatePackSpecificationForm,
    close: closeUpdatePackSpecificationForm,
  } = useToggle();
  const [selectedPackSpecForUpdate, setSelectedPackSpecForUpdate] = useState<SimplePackagingSpecification | null>(null);

  const form = useFormContext<BuyerMaterialSpecificationFormType>();
  const buyerId = useWatch({
    control: form.control,
    name: 'buyer_id',
  });
  const { isLoading, data } = useApiTradeV1TradeCompaniesPackagingSpecificationsRetrieve(buyerId);

  const packagingSpecificationsList = data?.results || [];
  const selectedPackagingSpecifications =
    useWatch({
      control: form.control,
      name: 'packaging_specification_ids',
    }) || [];

  const { deletePackagingSpecification } = useDeletePackagingSpecification({
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: getApiTradeV1TradeCompaniesPackagingSpecificationsRetrieveQueryKey(buyerId),
      }),
  });

  const isPackagingSpecificationsSelected = (specId: number) => {
    return selectedPackagingSpecifications?.some((spec) => spec === specId);
  };

  const onSuccessCreatePackagingSpecification = (newPackSpecId: number) => {
    closeCreatePackSpecificationForm();
    form.setValue('packaging_specification_ids', [...selectedPackagingSpecifications, newPackSpecId]);
    queryClient.invalidateQueries({
      queryKey: getApiTradeV1TradeCompaniesPackagingSpecificationsRetrieveQueryKey(buyerId),
    });
  };

  const handleSelectPackagingSpecification = (specId: number) => {
    if (isPackagingSpecificationsSelected(specId)) {
      form.setValue(
        'packaging_specification_ids',
        selectedPackagingSpecifications.filter((spec) => spec !== specId)
      );
    } else {
      form.setValue('packaging_specification_ids', [...selectedPackagingSpecifications, specId]);
    }
  };

  const handleDeletePackagingSpecification = (specId: number) => {
    if (isPackagingSpecificationsSelected(specId)) {
      form.setValue(
        'packaging_specification_ids',
        selectedPackagingSpecifications.filter((spec) => spec !== specId)
      );
    }
    deletePackagingSpecification(specId);
  };

  const handleUpdatePackagingSpecification = (specId: number) => {
    const selectedPackSpec = packagingSpecificationsList.find((spec) => spec.id === specId);
    if (!selectedPackSpec) {
      return;
    }
    setSelectedPackSpecForUpdate(selectedPackSpec);
    openUpdatePackSpecificationForm();
  };

  const onUpdatePackagingSpecificationSuccess = () => {
    queryClient.invalidateQueries({
      queryKey: getApiTradeV1TradeCompaniesPackagingSpecificationsRetrieveQueryKey(buyerId),
    });
  };

  return (
    <Stack
      spacing={2}
      sx={{
        marginBottom: 2,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {t('buyerMaterialSpecifications.packagingSpecificationList.title')}
        </Typography>
        <Button
          variant="secondary"
          onClick={openCreatePackSpecificationForm}
          startIcon={<Add />}
          aria-label={t('buyerMaterialSpecifications.packagingSpecificationList.actions.createAriaLabel')}
        >
          {t('buyerMaterialSpecifications.packagingSpecificationList.actions.create')}
        </Button>
        <CreatePackagingSpecificationDialog
          open={isCreatePackSpecificationsFormOpen}
          onClose={closeCreatePackSpecificationForm}
          buyerId={buyerId}
          onSuccess={onSuccessCreatePackagingSpecification}
        />
        {selectedPackSpecForUpdate ? (
          <UpdatePackagingSpecificationDialog
            key={selectedPackSpecForUpdate.id}
            open={isUpdatePackSpecificationsFormOpen}
            onClose={closeUpdatePackSpecificationForm}
            packagingSpecification={selectedPackSpecForUpdate}
            onSuccess={onUpdatePackagingSpecificationSuccess}
          />
        ) : null}
      </Stack>
      {isLoading ? <CardsListSkeleton cardCount={3} cardRows={1} /> : null}
      {!isLoading && packagingSpecificationsList?.length === 0 ? <NoPackagingSpecifications /> : null}
      <Stack
        direction="row"
        sx={{
          flexWrap: 'nowrap',
          gap: 2,

          overflowX: 'auto',

          // Add padding bottom for scrollbar space
          pb: 1,
        }}
      >
        {packagingSpecificationsList?.map((packagingSpecification, index) => (
          <PackagingSpecificationsCard
            key={index}
            packagingSpecifications={packagingSpecification}
            isSelected={isPackagingSpecificationsSelected(packagingSpecification.id)}
            onSelect={handleSelectPackagingSpecification}
            onClickEdit={handleUpdatePackagingSpecification}
            onClickDelete={handleDeletePackagingSpecification}
          />
        ))}
      </Stack>
    </Stack>
  );
};
